import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users, SquarePercent, MessageCircle } from 'lucide-react';

interface JoinPromptProps {
  communityName: string;
  onJoin: () => Promise<void>;
  isJoining: boolean;
}

export function JoinPrompt({ communityName, onJoin, isJoining }: JoinPromptProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[400px] flex items-center justify-center px-4 sm:px-6 py-8 bg-white">
      <Card className="w-full max-w-md rounded-2xl border border-border shadow-md bg-white">
      <CardHeader className="text-center">
        <CardTitle className="text-3xl font-semibold tracking-tight text-gray-800">
          👋 Welcome to {communityName}
        </CardTitle>
        <CardDescription className="mt-2 text-gray-700">
          Get access to trusted local services, exclusive discounts, and a growing community built on shared values.
        </CardDescription>
      </CardHeader>
      <CardContent className="mt-4 space-y-3">
        <Feature icon={<Users className="h-4 w-4" />} text="Community-approved service providers" />
        <Feature icon={<SquarePercent className="h-4 w-4" />} text="Exclusive member discounts" />
        <Feature icon={<MessageCircle className="h-4 w-4" />} text="Direct messaging with service pros" />
      </CardContent>
      <CardFooter className="flex flex-col gap-2 mt-6">
        <Button onClick={onJoin} disabled={isJoining} className="w-full text-base rounded-xl">
          {isJoining ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Joining...
            </>
          ) : (
            'Join Community'
          )}
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="w-full text-sm text-muted-foreground hover:text-primary"
        >
          Return to Home
        </Button>
      </CardFooter>
      </Card>
    </div>
  );
}

function Feature({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-3 text-muted-foreground text-sm">
      <span className="text-primary">{icon}</span>
      <span>{text}</span>
    </div>
  );
}
